import { ChevronDown } from 'lucide-react';
import type { CSSProperties, HTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import styles from './Controls.module.css';

const cx = (...parts: Array<string | false | undefined | null>) => parts.filter(Boolean).join(' ');

// Chip selector

export interface ChipOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface ChipSelectorProps<T extends string> {
  options: readonly ChipOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
  'aria-label'?: string;
}

/** Single-select pill buttons. Selected is solid indigo. */
export function ChipSelector<T extends string>({ options, value, onChange, disabled, ...rest }: ChipSelectorProps<T>) {
  return (
    <div className={styles.chips} role="radiogroup" aria-label={rest['aria-label']}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={cx(styles.chip, o.value === value && styles.chipSelected)}
          disabled={disabled || o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface MultiChipSelectorProps<T extends string> {
  options: readonly ChipOption<T>[];
  values: readonly T[];
  onChange: (values: T[]) => void;
  disabled?: boolean;
  'aria-label'?: string;
}

export function MultiChipSelector<T extends string>({ options, values, onChange, disabled, ...rest }: MultiChipSelectorProps<T>) {
  const toggle = (v: T) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div className={styles.chips} role="group" aria-label={rest['aria-label']}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={values.includes(o.value)}
          className={cx(styles.chip, values.includes(o.value) && styles.chipSelected)}
          disabled={disabled || o.disabled}
          onClick={() => toggle(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Toggle

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  'aria-label'?: string;
  id?: string;
}

/** The 42 by 24 switch. */
export function Toggle({ checked, onChange, disabled, id, ...rest }: ToggleProps) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={rest['aria-label']}
      className={cx(styles.switch, checked && styles.switchOn)}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.knob} />
    </button>
  );
}

export interface ToggleRowProps extends ToggleProps {
  label: ReactNode;
  note?: ReactNode;
}

/** Switch, then a label with a note beneath. */
export function ToggleRow({ label, note, ...toggle }: ToggleRowProps) {
  const id = useId();
  return (
    <div className={cx(styles.toggleRow, Boolean(note) && styles.toggleRowTop)}>
      <Toggle id={id} {...toggle} />
      <label htmlFor={id} className={styles.toggleText}>
        <span className={styles.toggleLabel}>{label}</span>
        {note && <span className={styles.toggleNote}>{note}</span>}
      </label>
    </div>
  );
}

// Progress

export interface ProgressBarProps {
  /** 0 to 1. */
  value: number;
  height?: 6 | 8;
  track?: 'accent' | 'white' | 'neutral';
  /** Turns warning above 80 or 85 percent when the bar represents consumption. */
  warnAbove?: number;
  tone?: 'after' | 'now';
  'aria-label'?: string;
}

export function ProgressBar({ value, height = 6, track = 'accent', warnAbove, tone = 'after', ...rest }: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(1, value));
  const warning = warnAbove !== undefined && clamped > warnAbove;
  return (
    <div
      className={cx(styles.track, height === 8 && styles.track8, track === 'white' && styles.trackWhite, track === 'neutral' && styles.trackNeutral)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped * 100)}
      aria-label={rest['aria-label']}
    >
      <div className={cx(styles.fill, warning && styles.fillWarning, tone === 'now' && styles.fillNow)} style={{ width: `${clamped * 100}%` }} />
    </div>
  );
}

// Fields

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  chips?: boolean;
  htmlFor?: string;
  children: ReactNode;
}

export function Field({ label, hint, chips, htmlFor, children }: FieldProps) {
  return (
    <div className={cx(styles.field, chips && styles.fieldChips)}>
      <label className={styles.label} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && <div className={styles.hint}>{hint}</div>}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  mono?: boolean;
  invalid?: boolean;
}

export function TextInput({ mono, invalid, className, ...rest }: TextInputProps) {
  return <input className={cx(styles.input, mono && styles.inputMono, invalid && styles.inputError, className)} {...rest} />;
}

export interface SelectProps<T extends string> extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> {
  options: readonly T[] | ReadonlyArray<{ value: T; label: string }>;
  value: T | '';
  onChange: (value: T) => void;
  placeholder?: string;
}

/** Native select for long lists, styled like the text input. */
export function Select<T extends string>({ options, value, onChange, placeholder, className, ...rest }: SelectProps<T>) {
  const items = (options as ReadonlyArray<T | { value: T; label: string }>).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  return (
    <div className={styles.selectWrap}>
      <select className={cx(styles.input, styles.select, className)} value={value} onChange={(e) => onChange(e.target.value as T)} {...rest}>
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {items.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown size={15} strokeWidth={1.75} aria-hidden="true" className={styles.selectIcon} />
    </div>
  );
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(styles.input, styles.textarea, className)} {...rest} />;
}

/** Inline error, never a native dialog. */
export function InlineError({ children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={styles.error} role="alert" {...rest}>
      {children}
    </div>
  );
}

export function InlineInfo({ children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={styles.info} {...rest}>
      {children}
    </div>
  );
}

export function WarningPanel({ children, style, ...rest }: HTMLAttributes<HTMLDivElement> & { style?: CSSProperties }) {
  return (
    <div className={styles.warningPanel} style={style} {...rest}>
      {children}
    </div>
  );
}

export function Actions({ children, className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx(styles.actions, className)} {...rest}>
      {children}
    </div>
  );
}
